import type { CrossCheckClause } from '../types/crossCheck';
import { TrackChangesParser } from '../word/utils/TrackChangesParser';
import {
  type CrossCheckFeatureContext,
  isMccTableRowSupportEnabled,
} from './crossCheckSolution';
import {
  extractClauseNumberFromLabelContext,
  isLikelyClauseReferenceText,
} from './clauseLabelUtils';

export type { CrossCheckFeatureContext, CrossCheckSolution } from './crossCheckSolution';
export {
  crossCheckSolutionFromDomain,
  crossCheckSolutionFromKebabVariant,
  isMccTableRowSupportEnabled,
} from './crossCheckSolution';

export {
  extractPrimarySectionNumber,
  extractClauseNumberFromLabelContext,
  isLikelyClauseReferenceText,
} from './clauseLabelUtils';

export type TrackChangesPart = {
  paragraph_index?: number;
  paragraph_xml?: string;
  edited_text?: string;
  role?: 'request' | 'clause_ref' | string;
};

export function getTrackChangesParts(clause: CrossCheckClause): TrackChangesPart[] {
  const parts = clause.track_changes?.track_changes_parts;
  return Array.isArray(parts) ? (parts as TrackChangesPart[]) : [];
}

function isClauseRefPart(part: TrackChangesPart): boolean {
  if (part?.role === 'clause_ref') return true;
  if (part?.role === 'request') return false;
  return isLikelyClauseReferenceText(part?.edited_text);
}

/** Parts shown in the Client Request card (request body only, unless row is clause-ref-only). MCC table rows only. */
export function getDisplayTrackChangesParts(
  clause: CrossCheckClause,
  ctx?: CrossCheckFeatureContext,
): TrackChangesPart[] {
  if (!isTableRowClause(clause, ctx)) return [];
  const parts = getTrackChangesParts(clause);
  const requestParts = parts.filter((part) => part?.role === 'request');
  if (requestParts.length > 0) return requestParts;
  const nonClauseRefParts = parts.filter((part) => !isClauseRefPart(part));
  return nonClauseRefParts.length > 0 ? nonClauseRefParts : parts;
}

/** Prefer inherited table-row anchor over Gemini/static next-row labels (e.g. 14.3 vs 15.3). */
export function resolveTableRowClauseNumber(
  clauseNumber: string | undefined,
  trackChanges: CrossCheckClause['track_changes'],
  ctx?: CrossCheckFeatureContext,
): string | undefined {
  if (!isMccTableRowSupportEnabled(ctx?.solution)) {
    return clauseNumber;
  }
  const tc = trackChanges;
  if (!tc || typeof tc !== 'object' || !(tc as { is_table_row?: boolean }).is_table_row) {
    return clauseNumber;
  }
  const anchor =
    typeof (tc as { inherited_clause_anchor?: string }).inherited_clause_anchor === 'string'
      ? (tc as { inherited_clause_anchor: string }).inherited_clause_anchor.trim()
      : '';
  if (anchor) return anchor;
  return clauseNumber;
}

export function isTableRowClause(
  clause: CrossCheckClause,
  ctx?: CrossCheckFeatureContext,
): boolean {
  if (!isMccTableRowSupportEnabled(ctx?.solution)) {
    return false;
  }
  const tc = clause.track_changes;
  return Boolean(
    tc &&
      typeof tc === 'object' &&
      (tc as { is_table_row?: boolean }).is_table_row === true
  );
}

export function usesOoxmlParagraphIndex(
  clause: CrossCheckClause,
  ctx?: CrossCheckFeatureContext,
): boolean {
  return (
    clause.module_type === 'module_b' ||
    isTableRowClause(clause, ctx) ||
    clause.track_changes?.paragraph_index != null
  );
}

function plainTextFromClientRequestField(value: string): string {
  const trimmed = (value || '').trim();
  if (!trimmed) return '';
  const isXml =
    trimmed.startsWith('<w:') ||
    trimmed.startsWith('<?xml') ||
    trimmed.includes('<w:p') ||
    trimmed.includes('<p ');
  if (isXml) {
    try {
      return TrackChangesParser.extractAcceptedText(trimmed).replace(/\s+/g, ' ').trim();
    } catch {
      return '';
    }
  }
  return trimmed.replace(/\s+/g, ' ').trim();
}

/** Extract clause number from table row context when Gemini returns לא נמצא. */
export function extractClauseFromRowContext(rowContextText: string | undefined | null): string {
  return extractClauseNumberFromLabelContext(rowContextText);
}

/**
 * Best plain-text anchor for navigating to this Module B / table TC paragraph.
 * Prefers edited_text; extracts from paragraph XML when client_request holds OOXML.
 */
export function getModuleBNavigationAnchor(
  clause: CrossCheckClause,
  ctx?: CrossCheckFeatureContext,
): string {
  const displayParts = getDisplayTrackChangesParts(clause, ctx);
  for (const part of displayParts) {
    const fromPart = (part.edited_text || '').replace(/\s+/g, ' ').trim();
    if (fromPart.length >= 15) return fromPart;
    const fromXml = plainTextFromClientRequestField(part.paragraph_xml || '');
    if (fromXml.length >= 15) return fromXml;
  }

  const fromEdited = (clause.edited_text || clause.text || '').replace(/\s+/g, ' ').trim();
  if (fromEdited.length >= 15) return fromEdited;

  const fromClient = plainTextFromClientRequestField(clause.client_request || '');
  if (fromClient.length >= 15) return fromClient;

  const fromTrackXml = plainTextFromClientRequestField(
    typeof clause.track_changes?.paragraph_xml === 'string'
      ? clause.track_changes.paragraph_xml
      : '',
  );
  if (fromTrackXml.length >= 15) return fromTrackXml;

  return fromEdited || fromClient || fromTrackXml;
}

/**
 * Per-cell search anchors for table rows (combined row text does not exist in one Word paragraph).
 */
export function getTableRowSearchAnchors(
  clause: CrossCheckClause,
  ctx?: CrossCheckFeatureContext,
): string[] {
  if (!isTableRowClause(clause, ctx)) return [];
  const anchors: string[] = [];
  const primary = getModuleBNavigationAnchor(clause, ctx);
  if (primary.length >= 15) {
    anchors.push(primary);
  }

  const tc = clause.track_changes;
  const paragraphIndex = clause.paragraph_index ?? tc?.paragraph_index;
  const rowParagraphs = tc?.row_paragraphs;
  if (Array.isArray(rowParagraphs) && paragraphIndex != null) {
    for (const row of rowParagraphs) {
      if (row?.paragraph_index !== paragraphIndex) continue;
      const t = String(row?.edited_text || row?.paragraph_text || '')
        .replace(/\s+/g, ' ')
        .trim();
      if (t.length >= 15) anchors.push(t);
    }
  }

  const rowContext =
    typeof clause.track_changes?.row_context_text === 'string'
      ? clause.track_changes.row_context_text.replace(/\s+/g, ' ').trim()
      : '';
  if (rowContext.length >= 10) {
    anchors.push(rowContext);
  }

  return [...new Set(anchors)];
}

/**
 * Combined plain text for table-row client requests (clause ref + request text
 * may span multiple cells; backend stores the merged string on edited_text/text).
 */
export function getTableRowCombinedText(clause: CrossCheckClause): string {
  return (clause.edited_text || clause.text || '').replace(/\s+/g, ' ').trim();
}

/**
 * Returns the client request XML string if the clause has XML content; otherwise null.
 */
export function getClientRequestXml(
  clause: CrossCheckClause,
  ctx?: CrossCheckFeatureContext,
): string | null {
  const displayParts = getDisplayTrackChangesParts(clause, ctx);
  if (displayParts.length > 0) {
    const xmls = displayParts
      .map((part) => part.paragraph_xml)
      .filter((xml): xml is string => typeof xml === 'string' && xml.trim().length > 0);
    if (xmls.length === 1) return xmls[0];
    if (xmls.length > 1) return xmls[0];
  }

  const cr = clause.client_request?.trim() || '';
  if (
    cr &&
    (cr.startsWith('<w:') ||
      cr.startsWith('<pkg:') ||
      cr.startsWith('<?xml') ||
      cr.includes('<w:p') ||
      cr.includes('<p '))
  ) {
    return cr;
  }
  if (clause.track_changes?.paragraph_xml) {
    return clause.track_changes.paragraph_xml;
  }
  return null;
}

export function resolveEffectiveParagraphIndex(clause: CrossCheckClause): number | null {
  const tc = clause.track_changes;
  const candidates = [
    clause.paragraph_index,
    tc?.paragraph_index,
    ...(Array.isArray(tc?.paragraph_indices) ? tc.paragraph_indices : []),
  ];
  for (const candidate of candidates) {
    if (candidate != null && !Number.isNaN(Number(candidate))) {
      return Number(candidate);
    }
  }
  return null;
}

/**
 * Returns plain text from the clause's client request suitable for searching
 * in the Word document (e.g. for paragraph index sync or navigate-by-content).
 */
export function getClientRequestSearchText(
  clause: CrossCheckClause,
  ctx?: CrossCheckFeatureContext,
): string {
  const tableRowEnabled = isMccTableRowSupportEnabled(ctx?.solution);
  if (clause.module_type === 'module_b' || (tableRowEnabled && isTableRowClause(clause, ctx))) {
    const anchor = getModuleBNavigationAnchor(clause, ctx);
    if (anchor) {
      return anchor;
    }
  }

  if (tableRowEnabled && isTableRowClause(clause, ctx)) {
    const anchors = getTableRowSearchAnchors(clause, ctx);
    const best = anchors[0] || getTableRowCombinedText(clause);
    if (best) {
      return best;
    }
  }

  const xml = getClientRequestXml(clause);
  let result = '';

  if (xml) {
    try {
      // Use extractAcceptedText (skips w:del content) so the search text matches
      // Word's para.text which also shows the accepted view (insertions only, no deletions).
      result = TrackChangesParser.extractAcceptedText(xml).replace(/\s+/g, ' ').trim();
    } catch {
      // fallthrough
    }
  }
  if (!result && clause.module_type === 'module_b' && clause.client_request) {
    try {
      const parsed = JSON.parse(clause.client_request);
      result = (
        (parsed.edited_text ?? parsed.original_text ?? clause.client_request) as string
      ).replace(/\s+/g, ' ').trim();
    } catch {
      result = (clause.client_request || '').replace(/\s+/g, ' ').trim();
    }
  }
  if (!result) {
    result = (clause.client_request || '').replace(/\s+/g, ' ').trim();
  }

  return result;
}

export type ParagraphSyncEntry = {
  id: string;
  searchText: string;
  existingIndex?: number;
  isTableRow?: boolean;
  paragraphIndices?: number[];
  searchAnchors?: string[];
};

/**
 * Build entries for {@link DocumentReader.syncParagraphIndicesFromDocument}.
 * Includes any persisted paragraph_index so bulk sync can verify/reuse it
 * instead of blindly fuzzy-matching hundreds of cards to the same paragraph.
 */
export function buildParagraphSyncEntries(
  clauses: CrossCheckClause[],
  ctx?: CrossCheckFeatureContext,
): ParagraphSyncEntry[] {
  return clauses
    .filter((c) => getClientRequestSearchText(c, ctx).trim().length > 0)
    .map((c) => {
      const resolvedIndex = resolveEffectiveParagraphIndex(c);
      const existingIndex =
        resolvedIndex != null ? resolvedIndex : undefined;
      const isTableRow = isTableRowClause(c, ctx);
      const paragraphIndices = Array.isArray(c.track_changes?.paragraph_indices)
        ? (c.track_changes.paragraph_indices as number[]).filter(
            (n) => typeof n === 'number' && !Number.isNaN(n),
          )
        : undefined;
      const searchAnchors = isTableRow ? getTableRowSearchAnchors(c, ctx) : undefined;
      return {
        id: c.id,
        searchText: getClientRequestSearchText(c, ctx),
        ...(existingIndex !== undefined && { existingIndex }),
        ...(isTableRow && { isTableRow: true }),
        ...(paragraphIndices?.length && { paragraphIndices }),
        ...(searchAnchors?.length && { searchAnchors }),
      };
    });
}
